/**
 * RequestAccessPage Component
 *
 * Public form for requesting access to LEXe Platform.
 * Follows LEXe design system with glassmorphism and animations.
 */

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  UserPlus,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Building2,
  Mail,
  User,
} from "lucide-react";

import { GlassCard } from "@/components/effects/GlassCard";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * Animation variants for staggered entry
 */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  reason: string;
}

const initialFormData: FormData = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  reason: "",
};

/**
 * RequestAccessPage Component
 */
export function RequestAccessPage() {
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Handle form field change
   */
  const handleChange = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  /**
   * Validate form
   */
  const validateForm = useCallback((): boolean => {
    if (!formData.email.trim()) {
      setError(t("auth.errors.email_required", "Email is required"));
      return false;
    }
    if (!formData.email.includes("@")) {
      setError(t("auth.errors.email_invalid", "Please enter a valid email"));
      return false;
    }
    if (!formData.firstName.trim()) {
      setError(t("auth.errors.first_name_required", "First name is required"));
      return false;
    }
    if (!formData.lastName.trim()) {
      setError(t("auth.errors.last_name_required", "Last name is required"));
      return false;
    }
    return true;
  }, [formData, t]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) return;

      setError(null);
      setIsSubmitting(true);

      try {
        const apiUrl =
          import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

        const response = await fetch(`${apiUrl}/identity/access-requests`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email.trim().toLowerCase(),
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            company: formData.company.trim() || null,
            reason: formData.reason.trim() || null,
          }),
        });

        if (response.ok) {
          setSuccess(true);
        } else {
          const data = await response.json();
          if (response.status === 409) {
            setError(
              t(
                "auth.errors.request_exists",
                "A request already exists for this email",
              ),
            );
          } else {
            setError(
              data.detail ||
                t("auth.errors.request_failed", "Failed to submit request"),
            );
          }
        }
      } catch {
        setError(t("auth.errors.network", "Network error. Please try again."));
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, t],
  );

  return (
    <div
      className={cn(
        "min-h-screen flex items-center justify-center p-4",
        "bg-gradient-to-br from-leo-dark via-leo-primary/30 to-leo-dark",
      )}
    >
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-br from-leo-accent to-transparent blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-tr from-leo-secondary to-transparent blur-3xl"
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative w-full max-w-md"
      >
        {/* Logo and branding */}
        <motion.div variants={itemVariants} className="text-center mb-8">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              delay: 0.1,
            }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-leo-primary via-leo-accent to-leo-secondary shadow-2xl shadow-leo-primary/40 mb-4"
          >
            <span className="text-4xl font-bold text-white font-heading">
              L
            </span>
          </motion.div>
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-white via-leo-light to-white/80 bg-clip-text text-transparent">
            {t("auth.request_access_title", "Request Access")}
          </h1>
          <p className="text-leo-gray mt-2">
            {t(
              "auth.request_access_subtitle",
              "Fill out the form below to request access",
            )}
          </p>
        </motion.div>

        {/* Form card */}
        <motion.div variants={itemVariants}>
          <GlassCard
            variant="dark"
            padding="lg"
            glow
            className="border border-white/10"
          >
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-leo-accent to-emerald-400 flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </motion.div>
                  <h2 className="text-xl font-heading font-semibold text-white mb-2">
                    {t("auth.request_submitted", "Request Submitted!")}
                  </h2>
                  <p className="text-leo-gray mb-6">
                    {t(
                      "auth.request_submitted_message",
                      "We'll review your request and get back to you soon.",
                    )}
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-leo-accent hover:text-leo-accent/80 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t("auth.back_to_login", "Back to Sign In")}
                  </Link>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  {/* Error message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-leo-gray mb-1.5">
                      {t("auth.email", "Email")} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-leo-gray/50" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="you@company.com"
                        className={cn(
                          "w-full pl-10 pr-4 py-3 rounded-lg",
                          "bg-white/5 border border-white/10",
                          "text-white placeholder:text-leo-gray/50",
                          "focus:outline-none focus:border-leo-accent/50 focus:ring-1 focus:ring-leo-accent/30",
                          "transition-all duration-200",
                        )}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-leo-gray mb-1.5">
                        {t("auth.first_name", "First Name")} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-leo-gray/50" />
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) =>
                            handleChange("firstName", e.target.value)
                          }
                          placeholder="John"
                          className={cn(
                            "w-full pl-10 pr-4 py-3 rounded-lg",
                            "bg-white/5 border border-white/10",
                            "text-white placeholder:text-leo-gray/50",
                            "focus:outline-none focus:border-leo-accent/50 focus:ring-1 focus:ring-leo-accent/30",
                            "transition-all duration-200",
                          )}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-leo-gray mb-1.5">
                        {t("auth.last_name", "Last Name")} *
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleChange("lastName", e.target.value)
                        }
                        placeholder="Doe"
                        className={cn(
                          "w-full px-4 py-3 rounded-lg",
                          "bg-white/5 border border-white/10",
                          "text-white placeholder:text-leo-gray/50",
                          "focus:outline-none focus:border-leo-accent/50 focus:ring-1 focus:ring-leo-accent/30",
                          "transition-all duration-200",
                        )}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-leo-gray mb-1.5">
                      {t("auth.company", "Company")}
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-leo-gray/50" />
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) =>
                          handleChange("company", e.target.value)
                        }
                        placeholder="ACME Corp"
                        className={cn(
                          "w-full pl-10 pr-4 py-3 rounded-lg",
                          "bg-white/5 border border-white/10",
                          "text-white placeholder:text-leo-gray/50",
                          "focus:outline-none focus:border-leo-accent/50 focus:ring-1 focus:ring-leo-accent/30",
                          "transition-all duration-200",
                        )}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-leo-gray mb-1.5">
                      {t("auth.reason", "Why do you need access?")}
                    </label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => handleChange("reason", e.target.value)}
                      placeholder={t(
                        "auth.reason_placeholder",
                        "Tell us briefly why you want to use LEXe...",
                      )}
                      rows={3}
                      className={cn(
                        "w-full px-4 py-3 rounded-lg resize-none",
                        "bg-white/5 border border-white/10",
                        "text-white placeholder:text-leo-gray/50",
                        "focus:outline-none focus:border-leo-accent/50 focus:ring-1 focus:ring-leo-accent/30",
                        "transition-all duration-200",
                      )}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    leftIcon={
                      !isSubmitting ? (
                        <UserPlus className="w-5 h-5" />
                      ) : undefined
                    }
                    data-testid="request-access-button"
                  >
                    {t("auth.submit_request", "Submit Request")}
                  </Button>

                  {/* Back to login */}
                  <div className="text-center pt-2">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1.5 text-sm text-leo-gray hover:text-leo-accent transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      {t("auth.back_to_login", "Back to Sign In")}
                    </Link>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </GlassCard>
        </motion.div>

        {/* Footer */}
        <motion.p
          variants={itemVariants}
          className="text-center text-xs text-leo-gray/60 mt-6"
        >
          {t("app.tagline", "From chaos to results")}
        </motion.p>
      </motion.div>
    </div>
  );
}

export default RequestAccessPage;
